import styled from 'styled-components'

import {
  ActionsWrapper,
  BackButtonWrapper,
  BadgesWrapper,
  Header as PageHeaderHeader,
  TitleWrapper,
  Toolbar,
} from '@/components/layout/page-header/page-header.styled'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Divider } from '@/components/ui/divider'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { ZoomFrame } from '@/components/ui/zoomable-image'
import { media } from '@/theme/common'

const borderRadius = '8'

export const Container = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['16'],
}))

// Note: below `md` the header regroups into 2 rows - title+status, then back+actions - which
// cuts across PageHeader's own title/actions split (backButton and title are siblings inside
// TitleWrapper today). CSS Grid with named areas lets each item move to whichever row it needs
// without touching PageHeader's DOM/props, same technique as ReservationsPageHeader in
// reservations.page.styled.ts. `display: contents` on TitleWrapper drops just its own box from
// layout so its children (BackButtonWrapper, the title) promote to being direct grid items of
// Toolbar, placeable by grid-area.
export const ReservationDetailsPageHeader = styled.div`
  ${media.down('md')} {
    ${PageHeaderHeader} {
      gap: ${({ theme }) => theme.space['8']};
    }

    ${Toolbar} {
      column-gap: ${({ theme }) => theme.space['12']};
      display: grid;
      grid-template-areas:
        'title badges'
        'back actions';
      grid-template-columns: 1fr auto;
      row-gap: ${({ theme }) => theme.space['8']};
    }

    ${TitleWrapper} {
      display: contents;
    }

    ${BackButtonWrapper} {
      grid-area: back;
      // Note: grid items stretch to fill their area by default (justify-items: stretch on
      // Toolbar, unset) - without this the back button balloons to the full column width
      // instead of sizing to its own content.
      justify-self: start;
    }

    ${BadgesWrapper} {
      grid-area: badges;
      justify-content: flex-end;
    }

    ${ActionsWrapper} {
      grid-area: actions;
      justify-content: flex-end;
      margin-left: 0;
    }

    // Note: neither Typography nor Skeleton (the loading state PageHeader renders in their
    // place) is itself a styled-components identity, so they can't be used as tagged-template
    // selectors here - target their data attributes instead, same as ReservationsPageHeader does.
    [data-component-id='Typography'],
    [data-component-id='Skeleton'] {
      grid-area: title;
    }
  }
`

// Note: same Card, just with tighter padding (2rem -> 1.2rem) and gap (2rem -> 0.8rem) on
// mobile - scoped to this feature's own cards rather than changing Card itself, which has
// consumers app-wide. The gap is Card's own flex gap between its direct children (e.g. the
// "Booking information" title row and the table below it).
export const CompactCard = styled(Card)(({ theme }) => ({
  [media.down('sm')]: {
    gap: theme.space['8'],
    padding: theme.space['12'],
  },
  overflow: 'visible',
}))

// Note: groups the image + name/date/user block into one row that stays a row on desktop -
// on mobile it's flattened away entirely (see Header below), so this only matters above `sm`.
export const HeaderMain = styled.div(({ theme }) => ({
  display: 'flex',
  gap: theme.space['16'],
  minWidth: 0,
}))

export const HeaderImageFrame = styled(ZoomFrame)(({ theme }) => ({
  '&&': {
    borderRadius: theme.border.radius[borderRadius],
    display: 'block',
    flexShrink: 0,
    height: '10rem',
    width: '10rem',
  },

  [media.down('sm')]: {
    '&&': {
      height: 'auto',
      width: '100%',
    },
  },
}))

export const HeaderImage = styled.img(({ theme }) => ({
  borderRadius: theme.border.radius[borderRadius],
  display: 'block',
  height: '100%',
  objectFit: 'cover',
  width: '100%',

  // Note: a full-width banner on mobile (per the reflow in Header below), not a small thumbnail
  // - aspect-ratio keeps it a predictable shape instead of however tall the source image is.
  [media.down('sm')]: {
    aspectRatio: '2 / 1',
    height: 'auto',
  },
}))

export const BasicInfoContainer = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minWidth: 0,
}))

export const BasicInfo = styled(Typography)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.space[8],

  svg: {
    color: theme.colors.primary500,
    flexShrink: 0,
    fontSize: '2rem',
  },
}))

// Note: hidden (rather than restyled) on mobile - the paid row there is just label + amount
// (see PaidInfo below and Header's grid-area: paid), the status pill is dropped to keep that
// row to one line.
export const PaidBadgeWrapper = styled.div`
  ${media.down('sm')} {
    display: none;
  }
`

export const PaidInfo = styled.div(({ theme }) => ({
  alignItems: 'flex-end',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['4'],
  justifyContent: 'center',
  marginLeft: 'auto',

  // Note: label + amount side by side ("Amount paid ------ $53.55") rather than stacked, per
  // Header's mobile reading order below.
  [media.down('sm')]: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginLeft: 0,
  },
}))

export const RefundedIcon = styled(Icon)(({ theme }) => ({
  marginLeft: theme.space['4'],
}))

// Note: hidden by default - only shown (as the mobile row's divider) inside Header's
// media.down('sm') block below.
export const HeaderDivider = styled(Divider)`
  display: none;
`

// Note: below `sm` this reflows into a completely different reading order (title, then a
// full-width image, then date/user, then a divider, then the paid row) rather than the desktop
// image-beside-text row - too different from a reorder to express with flex-wrap, so it's a
// grid with named areas instead (same technique as ReservationDetailsPageHeader above).
// `display: contents` on HeaderMain/BasicInfoContainer drops just their own box so their
// children promote to being direct grid items of Header; BasicInfoContainer's three children
// (title, date, user) share the same Typography identity so they're targeted by position
// (:first-child etc.) rather than by selector, since CSS selectors match the DOM tree
// regardless of `display: contents` on an ancestor.
export const Header = styled(Card)`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.space['16']};
  height: fit-content;

  ${media.down('sm')} {
    display: grid;
    grid-template-areas:
      'title'
      'image'
      'date'
      'user'
      'divider'
      'paid';
    grid-template-columns: 1fr;
    padding: ${({ theme }) => theme.space['12']};
    row-gap: ${({ theme }) => theme.space['12']};

    ${HeaderMain},
    ${BasicInfoContainer} {
      display: contents;
    }

    ${BasicInfoContainer} > :first-child {
      grid-area: title;
    }

    ${BasicInfoContainer} > :nth-child(2) {
      grid-area: date;
    }

    ${BasicInfoContainer} > :nth-child(3) {
      grid-area: user;
    }

    ${HeaderImageFrame} {
      grid-area: image;
    }

    ${HeaderDivider} {
      display: block;
      grid-area: divider;
    }

    ${PaidInfo} {
      grid-area: paid;
    }
  }
`

export const Table = styled.div(({ theme }) => ({
  border: `1px solid ${theme.colors.gray400}`,
  borderRadius: theme.border.radius[borderRadius],
}))

export const Row = styled.div<{ $fullWidth?: boolean }>(({ theme }) => ({
  '&:last-child': {
    borderBottom: 'none',
  },
  alignItems: 'center',
  borderBottom: `1px solid ${theme.colors.gray400}`,
  display: 'flex',
  padding: theme.space['8'],

  // Note: alignItems overrides the base 'center' (tuned for the desktop row layout) to
  // 'stretch' here, not 'flex-start' - Value has no width of its own, so it needs the
  // cross-axis stretch to get a definite width for overflow-wrap: break-word to wrap long
  // unbroken strings (e.g. an email address) against. Without it, Value sizes to its own
  // content and can force the whole page wider instead of wrapping.
  [media.down('sm')]: {
    alignItems: 'stretch',
    flexDirection: 'column',
    gap: theme.space['2'],
    padding: theme.space['4'],
  },
}))

// Note: text-large is the one size variant matching this table's original fixed 16px/24px
// regular-weight text exactly - swapping the plain divs for Typography (via .attrs, so every
// existing <S.Label>/<S.Value> call site keeps working unchanged) picks up the same responsive
// shrink the rest of the app's text already has (16px desktop -> 14px tablet -> 12px mobile),
// instead of staying fixed at 16px regardless of viewport.
export const Label = styled(Typography).attrs({ color: 'primary500', size: 'text-large' })<{
  $width?: string
}>(({ $width }) => ({
  width: $width ?? '30%',

  [media.down('sm')]: {
    width: '100%',
  },
}))

export const Value = styled(Typography).attrs({ color: 'primary800', size: 'text-large' })<{
  $fullWidth?: boolean
}>(() => ({
  flex: 1,
  // Note: min-width: 0 is required for overflow-wrap: break-word to actually take effect here -
  // a flex item's automatic min-width otherwise floors at its content's min-content size, and
  // that calculation ignores break-word's line-break opportunities, so without this a long
  // unbroken string (e.g. an email) still forces the row wider instead of wrapping.
  minWidth: 0,
  overflowWrap: 'break-word',
}))

export const DocumentRow = styled.div(({ theme }) => ({
  '& > div:last-child': {
    flex: 1,
    minWidth: 0,
  },

  '& > div:last-child > p:first-child': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  alignItems: 'center',
  display: 'flex',
  flex: 1,
  gap: theme.space['16'],
  minWidth: 0,
}))

export const DocumentIcon = styled.div<{ $width?: string }>(({ theme }) => ({
  alignItems: 'center',
  background: theme.colors.gray100,
  borderRadius: theme.space[borderRadius],
  color: theme.colors.blue700,
  display: 'flex',
  fontSize: '2rem',
  height: '4rem',
  justifyContent: 'center',
  padding: theme.space['4'],
  width: '4rem',
}))

export const PaymentOverviewContainer = styled.div(({ theme }) => ({
  alignItems: 'center',
  border: `1px solid ${theme.colors.gray300}`,
  borderRadius: theme.space[borderRadius],
  display: 'flex',
  flex: 1,
  gap: theme.space['16'],
  padding: theme.space['16'],

  [media.down('sm')]: {
    alignItems: 'stretch',
    flexDirection: 'column',
    padding: theme.space['8'],
  },
}))

export const PaymentOverviewItem = styled.div(({ theme }) => ({
  '& > p:first-of-type': {
    display: 'flex',
    gap: theme.space['4'],
    textAlign: 'center',
    whiteSpace: 'nowrap',
    wordBreak: 'keep-all',
  },
  '&:last-child': {
    borderRight: 'none',
  },
  alignItems: 'center',
  borderRight: `1px solid ${theme.colors.gray300}`,
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: theme.space['4'],

  [media.down('sm')]: {
    '&:last-child': {
      borderBottom: 'none',
      paddingBottom: 0,
    },
    alignItems: 'flex-start',
    borderBottom: `1px solid ${theme.colors.gray300}`,
    borderRight: 'none',
    paddingBottom: theme.space['12'],
  },
}))

export const NotesContainer = styled.div(({ theme }) => ({
  border: `1px solid ${theme.colors.gray300}`,
  borderRadius: theme.space[borderRadius],
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['12'],
  padding: theme.space['16'],

  [media.down('sm')]: {
    padding: theme.space['8'],
  },
}))

export const Note = styled.div<{ $noBackground?: boolean }>(({ theme, $noBackground }) => ({
  '& p': {
    wordBreak: 'break-word',
  },
  background: $noBackground ? 'none' : theme.colors.gray100,
  border: `1px solid ${theme.colors.gray300}`,
  borderRadius: theme.space[borderRadius],
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['8'],
  padding: theme.space['16'],

  [media.down('sm')]: {
    padding: theme.space['8'],
  },
}))

export const NoteHeader = styled.div(({ theme }) => ({
  '& > div': {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.space['8'],
  },

  '& > p:first-of-type': {
    minWidth: 0,
    overflowWrap: 'break-word',
  },

  '& button:first-of-type': {
    color: theme.colors.primary800,
  },
  '& button:last-of-type': {
    color: theme.colors.red500,
  },

  alignItems: 'center',
  display: 'flex',
  gap: theme.space['8'],
  justifyContent: 'space-between',
}))

export const NoNotesContainer = styled.div(() => ({
  '& > img': {
    height: '10rem',
    width: '10rem',
  },
  '& > p': {
    width: '25rem',
  },
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  justifyContent: 'center',
  minHeight: '25rem',
  textAlign: 'center',
}))

export const AddNewNoteButton = styled(Button)(() => ({
  position: 'absolute',
  right: '2rem',
  top: '2rem',

  // Note: Card (the positioning context for the absolute placement above) is a flex column
  // with a gap - static here just lets this button flow as a normal row after the title
  // instead of floating over it on a narrower Card.
  [media.down('sm')]: {
    position: 'static',
    width: '100%',
  },
}))

export const FormButtonsContainer = styled.div(({ theme }) => ({
  display: 'flex',
  gap: theme.space['8'],
  position: 'absolute',
  right: '2rem',
  top: '2rem',

  // Note: same reasoning as AddNewNoteButton above - static here places these buttons in their
  // actual DOM position (right after the note body field, inside the form) instead of floating
  // over the Card's title.
  [media.down('sm')]: {
    justifyContent: 'flex-end',
    position: 'static',
  },
}))

export const CustomerEmail = styled.a(() => ({
  textDecoration: 'underline',
}))
